import { useState } from "react";
import PropTypes from "prop-types";

// Collapsible JSON tree used by the Network Diagnostics panel. Big arrays (an elset history can
// be tens of thousands of rows) are revealed a chunk at a time so opening a node stays cheap.
const CHUNK = 100;

const isExpandable = (value) => value !== null && typeof value === "object";

const summarize = (value) => {
    if (Array.isArray(value)) return `Array(${value.length})`;
    const keys = Object.keys(value);
    return keys.length === 1 ? "{1 key}" : `{${keys.length} keys}`;
};

const primitiveClass = (value) => {
    if (typeof value === "string") return "netdiag-json-string";
    if (typeof value === "number") return "netdiag-json-number";
    if (typeof value === "boolean") return "netdiag-json-bool";
    return "netdiag-json-null";
};

const formatPrimitive = (value) => {
    if (typeof value === "string") return `"${value}"`;
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    return String(value);
};

function JsonNode({ name, value, depth, defaultOpenDepth }) {
    const [open, setOpen] = useState(depth < defaultOpenDepth);
    const [limit, setLimit] = useState(CHUNK);
    const indent = { paddingLeft: `${depth * 14}px` };

    if (!isExpandable(value)) {
        return (
            <div className="netdiag-json-row" style={indent}>
                {name !== undefined && <span className="netdiag-json-key">{name}: </span>}
                <span className={primitiveClass(value)}>{formatPrimitive(value)}</span>
            </div>
        );
    }

    const children = Array.isArray(value)
        ? value.map((child, index) => [index, child])
        : Object.entries(value);
    const shown = children.slice(0, limit);
    const hidden = children.length - shown.length;

    return (
        <div>
            <button
                type="button"
                className="netdiag-json-toggle"
                style={indent}
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
            >
                <span className="netdiag-json-caret">{open ? "▾" : "▸"}</span>
                {name !== undefined && <span className="netdiag-json-key">{name}: </span>}
                <span className="netdiag-json-summary">{summarize(value)}</span>
            </button>
            {open && shown.map(([key, child]) => (
                <JsonNode
                    key={key}
                    name={key}
                    value={child}
                    depth={depth + 1}
                    defaultOpenDepth={defaultOpenDepth}
                />
            ))}
            {open && hidden > 0 && (
                <button
                    type="button"
                    className="netdiag-json-more"
                    style={{ paddingLeft: `${(depth + 1) * 14}px` }}
                    onClick={() => setLimit((prev) => prev + CHUNK)}
                >
                    … show {Math.min(CHUNK, hidden)} more of {hidden}
                </button>
            )}
        </div>
    );
}

JsonNode.propTypes = {
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.any,
    depth: PropTypes.number.isRequired,
    defaultOpenDepth: PropTypes.number.isRequired,
};

const JsonView = ({ data, defaultOpenDepth = 1 }) => (
    <div className="netdiag-json">
        <JsonNode value={data} depth={0} defaultOpenDepth={defaultOpenDepth} />
    </div>
);

JsonView.propTypes = {
    data: PropTypes.any,
    defaultOpenDepth: PropTypes.number,
};

export default JsonView;
